import { EDITION_CAPABILITIES, type EditionCapabilities, type MeResponse } from '@dbrain/shared';
import { Dialog, IconButton, Icons, type IconComponent } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { useMe } from '../../api/hooks.js';
import { isDomainAdmin, isOrganizationAdmin } from '../../pages/admin/AdminLayout.js';
import { OrganizationContent } from '../../pages/TeamPage.js';
import { useCapabilities } from '../../experience.js';
import { settingsLink } from './links.js';
import { SubscriptionSection } from './SubscriptionSection.js';
import { AccountSection, GeneralSection, MemorySection, NotificationsSection } from './sections.js';

/**
 * The settings window (docs/design/mockups/Ayarlar.dc.html): the personal
 * sections on the left, one section on the right. Administration is a page
 * of its own (D-100): its admins and domain admins get one "Yönetim" link
 * here. It opens over the page it was called from.
 */
export type SectionId = 'general' | 'account' | 'notifications' | 'memory' | 'subscription' | 'organization';

// Phase 16a: "Abonelik" where the edition has plans, "Firma" where it has organizations
const PERSONAL: { id: SectionId; icon: IconComponent; needs?: 'plans' | 'organization' }[] = [
  { id: 'general', icon: Icons.settings },
  { id: 'account', icon: Icons.user },
  { id: 'notifications', icon: Icons.bell },
  { id: 'memory', icon: Icons.memory },
  { id: 'subscription', icon: Icons.gauge, needs: 'plans' },
  { id: 'organization', icon: Icons.organization, needs: 'organization' },
];

export function sectionOf(pathname: string, capabilities: EditionCapabilities = EDITION_CAPABILITIES.kurumsal): SectionId {
  const wanted = pathname.split('/')[2] ?? 'general';
  return PERSONAL.find((s) => s.id === wanted && (!s.needs || capabilities[s.needs]))?.id ?? 'general';
}

function Content({ section, me }: { section: SectionId; me: MeResponse }) {
  switch (section) {
    case 'account':
      return <AccountSection me={me} />;
    case 'notifications':
      return <NotificationsSection />;
    case 'memory':
      return <MemorySection />;
    case 'organization':
      return <OrganizationContent me={me} />;
    case 'subscription':
      return <SubscriptionSection />;
    default:
      return <GeneralSection me={me} />;
  }
}

export function SettingsDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const me = useMe().data;
  const capabilities = useCapabilities();
  if (!me) return null;
  const section = sectionOf(location.pathname, capabilities);
  const management = capabilities.organization && (isOrganizationAdmin(me) || isDomainAdmin(me));
  const item = ({ id, icon: Icon }: { id: SectionId; icon: IconComponent }) => (
    <Link
      key={id}
      className="nav-item"
      aria-current={id === section ? 'page' : undefined}
      replace
      {...settingsLink(id, location)}
      data-testid={`settings-nav-${id}`}
    >
      <Icon size={20} />
      <span className="nav-item__label">{t(`settingsDialog.sections.${id}`)}</span>
    </Link>
  );
  return (
    <Dialog
      bare
      size="full"
      title={t('settingsDialog.title')}
      labelledBy="settings-title"
      closeLabel={t('settingsDialog.close')}
      onClose={onClose}
      dismissOnBackdrop
      className="settings-dialog"
      initialFocus=".settings-dialog__nav [aria-current='page']"
      data-testid="settings-dialog"
    >
      <nav className="settings-dialog__nav" aria-label={t('settingsDialog.sectionsLabel')}>
        <IconButton label={t('settingsDialog.close')} icon={<Icons.close />} onClick={onClose} className="settings-dialog__close" data-testid="settings-close" />
        {PERSONAL.filter((s) => !s.needs || capabilities[s.needs]).map(item)}
        {management ? (
          <>
            <p className="settings-dialog__group">{t('shell.management')}</p>
            <Link className="nav-item" to="/admin" data-testid="settings-nav-admin">
              <Icons.access size={20} />
              <span className="nav-item__label">{t('settingsDialog.openManagement')}</span>
              <Icons.chevronRight size={16} className="nav-item__end" />
            </Link>
          </>
        ) : null}
      </nav>
      <section className="settings-dialog__content" aria-labelledby="settings-title">
        <h2 id="settings-title" className="settings-dialog__title">
          {t(`settingsDialog.sections.${section}`)}
        </h2>
        <Content section={section} me={me} />
      </section>
    </Dialog>
  );
}
